import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { MantineProvider } from '@mantine/core'
import { Notifications } from '@mantine/notifications'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, OpenDocumentsProvider, ThemeProvider, DisplayCurrencyProvider } from '@/contexts'
import { RouterWithAuth } from './RouterWithAuth'
import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import './index.css'

// Caching strategy (FE-11):
// There is no normalized cache — the semantic layer returns flat rows that aren't
// entity-normalizable, so there is nothing to normalize. Reads (dashboards, the contract
// viewer, the vault list) fetch on mount; that refetch cost is acceptable because every read is bounded
// by the server's 500-row cap / folded-root pagination. projectService keeps a small ad-hoc TtlCache
// for project lists. TanStack Query is mounted here and ready for incremental useQuery adoption (keyed
// by [resource, projectUuid, params], invalidated after the REST writes) when a read needs dedup/caching
// beyond refetch-on-mount — no read uses it yet, so today's behaviour is a deliberate refetch-on-mount.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <MantineProvider>
        {/* Above the full-screen dialogs (relationship dialog 1000, its confirmations 1200), so a
            save notification's Undo stays clickable while the dialog is open (AER-5 REQ-9). */}
        <Notifications position="bottom-right" zIndex={1300} />
        <ThemeProvider>
          <DisplayCurrencyProvider>
            <AuthProvider>
              <OpenDocumentsProvider>
                <RouterWithAuth />
              </OpenDocumentsProvider>
            </AuthProvider>
          </DisplayCurrencyProvider>
        </ThemeProvider>
      </MantineProvider>
    </QueryClientProvider>
  </StrictMode>,
)
